import { useNavigate } from "react-router-dom";
import { Card, InsightCard, ProvenanceTag, RankList } from "@/ui";
import { channels, insights, topSources } from "@/mock";
import type { Row } from "@/mock";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useSources } from "@/data/hooks";
import { PanelHeader } from "../parts";
import { useSignalsQuery, matches } from "../searchContext";
import { AigenTag, EmptyState, SampleChip, Skeleton, panelState } from "../wiring";

export function Acquisition() {
  const nav = useNavigate();
  const insight = insights[2]!; // Product Hunt converting 2.3×
  const { siteId, range, live } = useWorkspace();

  const sourcesQ = useSources(siteId, range);
  const state = panelState({
    siteId,
    workspaceLive: live,
    isLoading: sourcesQ.isLoading,
    isError: sourcesQ.isError,
    hasData: !!sourcesQ.data,
  });

  const isLive = state === "live";

  const sourceRows: Row[] =
    isLive && sourcesQ.data?.items?.length
      ? sourcesQ.data.items.map((it) => ({ label: it.source ?? "(direct)", value: it.visits, pct: 0 }))
      : topSources;

  const q = useSignalsQuery();
  const channelRows = channels.filter((r) => matches(q, r.label));
  const srcShown = sourceRows.filter((r) => matches(q, r.label));
  const noData = isLive && (!sourcesQ.data || sourcesQ.data.items.length === 0);
  const srcEmpty = noData || (!!q && srcShown.length === 0);

  return (
    <div className="stagger space-y-6">
      <PanelHeader
        title="Acquisition"
        desc="Where visitors come from, and which channels are growing."
        action={
          isLive ? <AigenTag /> : (
            <span className="flex items-center gap-2">
              <ProvenanceTag source="matomo" />
              <SampleChip />
            </span>
          )
        }
      />

      {state === "loading" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-[260px]" />
          <Skeleton className="h-[260px]" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {/* Channels: no first-party channel-grouping endpoint yet → sample. */}
          <RankList
            title="Channels"
            rows={channelRows}
            accentTop
            footer={
              <span className="flex items-center gap-2">
                <ProvenanceTag source="matomo" />
                <SampleChip />
              </span>
            }
          />
          {srcEmpty ? (
            <Card className="p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-[14px] font-semibold tracking-tight text-ink-strong">
                  Top sources
                </h3>
                <AigenTag />
              </div>
              {q && !noData ? (
                <EmptyState title={`No sources match “${q}”`} detail="Try a different search term." />
              ) : (
                <EmptyState title="No sources yet" detail="Referrers appear once traffic arrives." />
              )}
            </Card>
          ) : (
            <RankList
              title="Top sources"
              rows={srcShown}
              accentTop
              footer={
                isLive ? (
                  <AigenTag />
                ) : (
                  <span className="flex items-center gap-2">
                    <ProvenanceTag source="posthog" />
                    <SampleChip />
                  </span>
                )
              }
            />
          )}
        </div>
      )}

      <InsightCard
        insight={insight}
        onAsk={() => nav("/make", { state: { prompt: insight.makePrompt } })}
      />
    </div>
  );
}
